import { jsxs, jsx } from "react/jsx-runtime";
import { useRef, useState, useCallback, useLayoutEffect } from "react";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import "react-i18next";
import "../shared/quiz-style-context.js";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import "../../../context/DevToolsContext.js";
import "../../../utils/quiz-parser.js";
import { getImageUrl, getPrintScale, PrintQuizTitle, PrintQuizImage, PrintOptionImage, getStyles } from "./print-common.js";
const LINE_COLORS = ["#ff9f19", "#0066CC", "#00BFA0", "#E0457B", "#7B61FF", "#F5A623"];
function PairingPrint({
  quiz,
  number,
  showCorrectAnswers = false,
  correctAnswers,
  compact
}) {
  const s = getStyles(compact);
  const imageUrl = getImageUrl(quiz.image, quiz.imagePrint);
  const printScale = getPrintScale(quiz.image, quiz.imagePrint);
  const pairs = quiz.pairs;
  const maxRows = Math.max(pairs.left.length, pairs.right.length);
  const matches = [];
  if (showCorrectAnswers && correctAnswers) {
    correctAnswers.forEach((rightKey, leftIdx) => {
      if (!rightKey) return;
      const rightIdx = pairs.right.findIndex((r) => r.key === rightKey);
      if (rightIdx !== -1) matches.push([leftIdx, rightIdx]);
    });
  }
  const matchesSig = matches.map(([a, b]) => `${a}:${b}`).join("|");
  const gridRef = useRef(null);
  const leftDotRefs = useRef([]);
  const rightDotRefs = useRef([]);
  const matchesRef = useRef(matches);
  matchesRef.current = matches;
  const [lines, setLines] = useState([]);
  const recompute = useCallback(() => {
    const grid = gridRef.current;
    const m = matchesRef.current;
    if (!grid || m.length === 0) {
      setLines((prev) => prev.length === 0 ? prev : []);
      return;
    }
    const gridRect = grid.getBoundingClientRect();
    const next = [];
    m.forEach(([leftIdx, rightIdx], i) => {
      const l = leftDotRefs.current[leftIdx];
      const r = rightDotRefs.current[rightIdx];
      if (!l || !r) return;
      const lr = l.getBoundingClientRect();
      const rr = r.getBoundingClientRect();
      next.push({
        x1: lr.left + lr.width / 2 - gridRect.left,
        y1: lr.top + lr.height / 2 - gridRect.top,
        x2: rr.left + rr.width / 2 - gridRect.left,
        y2: rr.top + rr.height / 2 - gridRect.top,
        color: LINE_COLORS[i % LINE_COLORS.length]
      });
    });
    setLines(next);
  }, []);
  useLayoutEffect(() => {
    recompute();
    const grid = gridRef.current;
    if (!grid || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => recompute());
    ro.observe(grid);
    return () => ro.disconnect();
  }, [recompute, matchesSig, maxRows, compact]);
  return /* @__PURE__ */ jsxs("div", { className: cn(s.container, s.quizSection), children: [
    /* @__PURE__ */ jsx(PrintQuizTitle, { number, compact, children: /* @__PURE__ */ jsx(KatexText, { text: quiz.title }) }),
    imageUrl && /* @__PURE__ */ jsx(PrintQuizImage, { src: imageUrl, printScale }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        ref: gridRef,
        className: "w-full px-[40px]",
        style: { position: "relative", display: "grid", gridTemplateColumns: "200px 1fr 200px", gridAutoRows: "1fr", gap: "12px 0" },
        children: [
          Array.from({ length: maxRows }).map((_, rowIdx) => {
            const leftItem = pairs.left[rowIdx];
            const rightItem = pairs.right[rowIdx];
            const leftImageUrl = (leftItem == null ? void 0 : leftItem.image) ? getImageUrl(leftItem.image) : null;
            const rightImageUrl = (rightItem == null ? void 0 : rightItem.image) ? getImageUrl(rightItem.image) : null;
            return /* @__PURE__ */ jsxs("div", { className: "contents", children: [
              leftItem ? /* @__PURE__ */ jsxs(
                "div",
                {
                  className: cn(
                    "bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px]",
                    "flex items-center justify-center min-h-[64px] px-[12px] py-[8px]"
                  ),
                  children: [
                    leftImageUrl && /* @__PURE__ */ jsx(PrintOptionImage, { src: leftImageUrl, alt: "pairing-left", baseMaxHeightPx: 28 }),
                    leftItem.answer && /* @__PURE__ */ jsx("span", { className: cn("text-black", compact ? "text-[15px]" : "text-[14px]"), children: /* @__PURE__ */ jsx(KatexText, { text: leftItem.answer }) })
                  ]
                }
              ) : /* @__PURE__ */ jsx("div", { "aria-hidden": true }),
              /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between px-[10px]", children: [
                leftItem ? /* @__PURE__ */ jsx("span", { ref: (el) => {
                  leftDotRefs.current[rowIdx] = el;
                }, className: "size-[10px] rounded-full bg-[#ccc]" }) : /* @__PURE__ */ jsx("span", { className: "size-[10px]", "aria-hidden": true }),
                rightItem ? /* @__PURE__ */ jsx("span", { ref: (el) => {
                  rightDotRefs.current[rowIdx] = el;
                }, className: "size-[10px] rounded-full bg-[#ccc]" }) : /* @__PURE__ */ jsx("span", { className: "size-[10px]", "aria-hidden": true })
              ] }),
              rightItem ? /* @__PURE__ */ jsxs(
                "div",
                {
                  className: cn(
                    "bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px]",
                    "flex items-center justify-center min-h-[64px] px-[12px] py-[8px]"
                  ),
                  children: [
                    rightImageUrl && /* @__PURE__ */ jsx(PrintOptionImage, { src: rightImageUrl, alt: "pairing-right", baseMaxHeightPx: 28 }),
                    rightItem.answer && /* @__PURE__ */ jsx("span", { className: cn("text-black", compact ? "text-[15px]" : "text-[14px]"), children: /* @__PURE__ */ jsx(KatexText, { text: rightItem.answer }) })
                  ]
                }
              ) : /* @__PURE__ */ jsx("div", { "aria-hidden": true })
            ] }, rowIdx);
          }),
          lines.length > 0 && /* @__PURE__ */ jsx(
            "svg",
            {
              "aria-hidden": true,
              style: { position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible", pointerEvents: "none" },
              children: lines.map((ln, i) => /* @__PURE__ */ jsx("line", { x1: ln.x1, y1: ln.y1, x2: ln.x2, y2: ln.y2, stroke: ln.color, strokeWidth: 2, strokeLinecap: "round" }, i))
            }
          )
        ]
      }
    )
  ] });
}
export {
  PairingPrint
};
//# sourceMappingURL=pairing-print.js.map
